import React from 'react';
import { Box, Typography } from '@mui/material';
import { Product, PricingModel, PricingTier } from '../types';

interface ProductPricingProps {
  product: Product;
  colorConfig?: {
    bgColor: string;
    textColor: string;
  };
  selectedTier: PricingTier | null;
  setSelectedTier: (tier: PricingTier | null) => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
}

const ProductPricing: React.FC<ProductPricingProps> = ({
  product,
  colorConfig,
  selectedTier,
  quantity,
}) => {
  const { pricingData } = product;

  const formatPrice = (price: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const getPricingContent = () => {
    if (!pricingData) {
      return (
        <Box>
          <Typography
            variant="body2"
            color="text.primary"
            fontWeight={600}
            sx={{ mb: 0.5, fontSize: '0.9rem' }}
          >
            Custom Pricing Available
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.8rem', mb: 1.5 }}
          >
            Contact our sales team for a tailored quote based on your specific
            needs
          </Typography>
        </Box>
      );
    }

    const { pricingModel, priceDetails, currencyCode } = pricingData;

    if (pricingModel === PricingModel.Block && priceDetails.tiers) {
      return (
        <Box>
          {selectedTier && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    color: colorConfig?.bgColor || 'primary.main',
                    lineHeight: 1,
                  }}
                >
                  {formatPrice(selectedTier.price, currencyCode)}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: '1.25rem', fontWeight: 500 }}
                >
                  /yearly
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    if (pricingModel === PricingModel.Standard) {
      const isSubscription = priceDetails.isSubscription;
      const listPrice = priceDetails.listPrice || 0;
      const yearlyPrice = priceDetails.yearlyPrice || 0;

      if (isSubscription) {
        const annualPrice = quantity > 1 ? yearlyPrice * quantity : yearlyPrice;

        return (
          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}
            >
              <Typography
                variant="h3"
                fontWeight={700}
                sx={{
                  color: colorConfig?.bgColor || 'primary.main',
                  lineHeight: 1,
                }}
              >
                {formatPrice(listPrice, currencyCode)}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: '1.25rem', fontWeight: 500 }}
              >
                {product.quantityType
                  ? `/${product.quantityType.toLowerCase()}/month`
                  : '/month'}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '1rem', opacity: 0.8 }}
            >
              {formatPrice(annualPrice, currencyCode)} billed yearly
            </Typography>
          </Box>
        );
      } else {
        return (
          <Box>
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                color: colorConfig?.bgColor || 'primary.main',
                lineHeight: 1,
                mb: 0.5,
              }}
            >
              {yearlyPrice === 0
                ? 'Free'
                : formatPrice(yearlyPrice, currencyCode)}
            </Typography>
            {yearlyPrice > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.85rem', my: 1.5 }}
              >
                Single payment with no recurring charges or subscriptions
              </Typography>
            )}
          </Box>
        );
      }
    }

    return null;
  };

  return <Box>{getPricingContent()}</Box>;
};

export default ProductPricing;
